import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import type { Property } from "../../models/property.model";
import type { ApplicationUser } from "../../models/user.model";
import { propertyService } from "../../service/property.service";
import type { ListItem } from "../Controls/AutocompleteInput";
import Footer from "../Footer";
import PropertyMap from "./PeopertyMap";
import PropertyFilter from "./PropertyFilter";
import PropertyGrid from "./PropertyGrid";

const PropertyListBB: React.FC<{}> = () => {
  // Redux state
  const userRole = useSelector((state: any) => state.auth.userRole) ?? "";

  // Shared state for properties
  const [properties, setProperties] = useState<Property[]>([]);
  const [allProperties, setAllProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [userDetails, setUserDetails] = useState<ApplicationUser | null>(null);

  // Optional state for future enhancements - can be used for filters
  const [dateRange, setDateRange] = useState<[Date | null, Date | null] | null>(
    null
  );
  const [selectedRoomType, setSelectedRoomType] = useState<{
    id: string | number;
    name: string;
  } | null>(null);
  const [selectedPropertyId, setSelectedPropertyId] = useState<
    string | number | null
  >(null);
  const [routeRequest, setRouteRequest] = useState<{
    origin: string;
    destination: string;
  } | null>(null);

  // Filter state - similar to ListProperties
  const [cities, setCities] = useState<ListItem[]>([]);
  const [selectedCity, setSelectedCity] = useState<ListItem | null>(null);
  const [locations, setLocations] = useState<ListItem[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<ListItem[]>([]);
  const [propertyTypes, setPropertyTypes] = useState<ListItem[]>([]);
  const [selectedPropertyTypes, setSelectedPropertyTypes] = useState<
    ListItem[]
  >([]);
  const [roomTypes, setRoomTypes] = useState<ListItem[]>([]);
  const [selectedRoomTypes, setSelectedRoomTypes] = useState<ListItem[]>([]);
  const [categories, setCategories] = useState<ListItem[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<ListItem[]>([]);
  const [selectedDistance, setSelectedDistance] = useState<number | null>(null);

  // Track if data has been loaded to prevent duplicate API calls
  const [roomTypesLoaded, setRoomTypesLoaded] = useState(false);
  const [citiesLoaded, setCitiesLoaded] = useState(false);
  const [locationsLoaded, setLocationsLoaded] = useState(false);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [propertyTypesLoaded, setPropertyTypesLoaded] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingRoomTypes, setLoadingRoomTypes] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingPropertyTypes, setLoadingPropertyTypes] = useState(false);

  // Helper function to extract dropdown options from properties data
  const extractDropdownOptionsFromProperties = useCallback(
    (propertiesData: Property[]) => {
      if (!propertiesData || propertiesData.length === 0) return;

      // Extract unique cities
      const uniqueCities = new Map<number | string, string>();
      propertiesData.forEach((property) => {
        if (property.cityId) {
          const cityName =
            property.city?.name ||
            (property as any).City?.name ||
            (property as any).cityName ||
            `City ${property.cityId}`;
          uniqueCities.set(property.cityId, cityName);
        }
      });
      const citiesFromProperties = Array.from(uniqueCities.entries()).map(
        ([id, name]) => ({ id, name })
      );
      if (citiesFromProperties.length > 0) {
        setCities(citiesFromProperties);
        setCitiesLoaded(true);
      }

      // Extract unique locations
      const uniqueLocations = new Map<number | string, string>();
      propertiesData.forEach((property) => {
        if (property.locationId && property.location?.name) {
          uniqueLocations.set(property.locationId, property.location.name);
        }
      });
      const locationsFromProperties = Array.from(uniqueLocations.entries()).map(
        ([id, name]) => ({ id, name })
      );
      if (locationsFromProperties.length > 0) {
        setLocations(locationsFromProperties);
        setLocationsLoaded(true);
      }

      // Extract unique property types
      const uniquePropertyTypes = new Map<number | string, string>();
      propertiesData.forEach((property) => {
        if (property.propertyTypeId && property.propertyType?.name) {
          uniquePropertyTypes.set(
            property.propertyTypeId,
            property.propertyType.name
          );
        }
      });
      const propertyTypesFromProperties = Array.from(
        uniquePropertyTypes.entries()
      ).map(([id, name]) => ({ id, name }));
      if (propertyTypesFromProperties.length > 0) {
        setPropertyTypes(propertyTypesFromProperties);
        setPropertyTypesLoaded(true);
      }

      // Extract unique room types
      const uniqueRoomTypes = new Map<number | string, string>();
      propertiesData.forEach((property) => {
        if (property.roomTypes && Array.isArray(property.roomTypes)) {
          property.roomTypes.forEach((roomType: any) => {
            if (roomType.id && roomType.name) {
              uniqueRoomTypes.set(roomType.id, roomType.name);
            }
          });
        }
      });
      const roomTypesFromProperties = Array.from(uniqueRoomTypes.entries()).map(
        ([id, name]) => ({ id, name })
      );
      if (roomTypesFromProperties.length > 0) {
        setRoomTypes(roomTypesFromProperties);
        setRoomTypesLoaded(true);
      }

      // Extract unique categories from rooms
      const uniqueCategories = new Map<number | string, string>();
      propertiesData.forEach((property) => {
        const propertyWithRooms = property as any;
        if (propertyWithRooms.rooms && Array.isArray(propertyWithRooms.rooms)) {
          propertyWithRooms.rooms.forEach((room: any) => {
            if (room.category && room.category.id && room.category.name) {
              uniqueCategories.set(room.category.id, room.category.name);
            }
          });
        }
      });
      const categoriesFromProperties = Array.from(
        uniqueCategories.entries()
      ).map(([id, name]) => ({ id, name }));
      if (categoriesFromProperties.length > 0) {
        setCategories(categoriesFromProperties);
        setCategoriesLoaded(true);
      }
    },
    []
  );

  // Utility function to calculate distance between two coordinates using Haversine formula
  const calculateDistance = useCallback(
    (lat1: number, lon1: number, lat2: number, lon2: number): number => {
      const R = 6371; // Radius of the Earth in kilometers
      const dLat = ((lat2 - lat1) * Math.PI) / 180;
      const dLon = ((lon2 - lon1) * Math.PI) / 180;
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos((lat1 * Math.PI) / 180) *
          Math.cos((lat2 * Math.PI) / 180) *
          Math.sin(dLon / 2) *
          Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const distance = R * c; // Distance in kilometers
      return distance;
    },
    []
  );

  // Function to get coordinates from address string
  const getCoordinatesFromAddress = useCallback(
    (address: string): Promise<{ lat: number; lng: number }> => {
      const defaultCenter = { lat: 13.0827, lng: 80.2707 }; // Default to Chennai
      return new Promise((resolve) => {
        if (
          !(
            window.google &&
            window.google.maps &&
            typeof window.google.maps.Geocoder === "function"
          )
        ) {
          console.error("Google Maps API is not loaded");
          resolve(defaultCenter);
          return;
        }

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address }, (results, status) => {
          if (status === "OK" && results && results[0]) {
            const { lat, lng } = results[0].geometry.location;
            resolve({ lat: lat(), lng: lng() });
          } else {
            console.warn("Geocode failed for address:", address, status);
            resolve(defaultCenter);
          }
        });
      });
    },
    []
  );

  // Function to calculate distance between user address and property address
  const calculateDistanceToProperty = useCallback(
    async (propertyAddress: string): Promise<string> => {
      if (!userDetails?.address || userDetails.address.length === 0) {
        return "";
      }

      // Get user's primary address (first address in the array)
      const userAddress = userDetails.address[0];
      const userAddressString = [
        userAddress.doorNumber,
        userAddress.buildingName,
        userAddress.address1,
        userAddress.address2,
      ]
        .filter(Boolean)
        .join(", ");

      if (!userAddressString || !propertyAddress) {
        return "";
      }

      try {
        const [userCoords, propertyCoords] = await Promise.all([
          getCoordinatesFromAddress(userAddressString),
          getCoordinatesFromAddress(propertyAddress),
        ]);

        const distance = calculateDistance(
          userCoords.lat,
          userCoords.lng,
          propertyCoords.lat,
          propertyCoords.lng
        );

        return distance < 1
          ? `${Math.round(distance * 1000)} m away`
          : `${distance.toFixed(1)} Km away`;
      } catch (error) {
        console.error("Error calculating distance:", error);
        return "";
      }
    },
    [userDetails, calculateDistance, getCoordinatesFromAddress]
  );

  // Handler for when user clicks "View map" on a property
  const handlePropertyMapView = useCallback((propertyId: string | number) => {
    setSelectedPropertyId(propertyId);
    setRouteRequest(null); // clear any active route when only viewing map
  }, []);

  // Handler to build and show route to a property
  const handlePropertyRouteView = useCallback(
    (property: Property) => {
      if (!property?.address) return;
      if (!userDetails?.address || userDetails.address.length === 0) return;

      const ua = userDetails.address[0];
      // Build origin string using the same fields used for distance calculation
      const origin = [
        ua.doorNumber,
        ua.buildingName,
        ua.address1,
        ua.address2,
        ua.pincode,
      ]
        .filter(Boolean)
        .join(", ");
      const destination = property.address;

      setSelectedPropertyId(property.id ?? null);
      setRouteRequest({ origin, destination });
    },
    [userDetails]
  );

  // API call to load properties (centralized logic)
  useEffect(() => {
    setLoading(true);

    if (
      userRole === "" ||
      userRole === null ||
      typeof userRole === "undefined"
    ) {
      return;
    }

    // For B2BUser, load all eligible properties without requiring cityId
    if (userRole === "B2BUser") {
      propertyService
        .searchPropertiesForB2BUser("", "", "")
        .then((data) => {
          if (data.properties) {
            setAllProperties(data.properties);
            setProperties(data.properties);
            extractDropdownOptionsFromProperties(data.properties);
          }
          // Store user details if available
          if (data.user) {
            setUserDetails(data.user);
          }
        })
        .catch((error) => {
          console.error("Error loading properties:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      // For non-B2B users, set empty properties
      setProperties([]);
      setAllProperties([]);
      setLoading(false);
    }
  }, [userRole, extractDropdownOptionsFromProperties]);

  // Handler for applying filters
  const handleSearch = useCallback(() => {
    let filteredProperties = [...allProperties];

    // Filter by city if selected
    if (selectedCity) {
      filteredProperties = filteredProperties.filter(
        (p) => p.cityId === selectedCity.id
      );
    }

    // Filter by room types if selected (multi-select)
    if (selectedRoomTypes.length > 0) {
      const searchRoomTypeIds = selectedRoomTypes.map((rt) => rt.id);
      filteredProperties = filteredProperties.filter((p) =>
        p.roomTypes?.some((rt: any) => searchRoomTypeIds.includes(rt.id))
      );
    }

    // Filter by locations if selected (multi-select)
    if (selectedLocations.length > 0) {
      const locationIds = selectedLocations.map((loc) => loc.id);
      filteredProperties = filteredProperties.filter(
        (p) => p.locationId && locationIds.includes(p.locationId)
      );
    }

    // Filter by categories if selected (multi-select)
    if (selectedCategories.length > 0) {
      const categoryNames = selectedCategories.map((cat) =>
        String(cat.name || "").toLowerCase()
      );
      filteredProperties = filteredProperties.filter((p: any) => {
        const rooms = p.rooms || [];
        if (!Array.isArray(rooms)) return false;
        return rooms.some((r: any) => {
          const rn =
            (r && r.category && r.category.name) || r?.categoryName || "";
          return categoryNames.includes(String(rn).toLowerCase());
        });
      });
    }

    // Filter by property types if selected (multi-select)
    if (selectedPropertyTypes.length > 0) {
      const propertyTypeIds = selectedPropertyTypes.map((pt) => pt.id);
      filteredProperties = filteredProperties.filter(
        (p) => p.propertyTypeId && propertyTypeIds.includes(p.propertyTypeId)
      );
    }

    // Filter by distance if selected
    if (
      userRole === "B2BUser" &&
      selectedDistance &&
      userDetails?.address &&
      userDetails.address.length > 0
    ) {
      const userAddress = userDetails.address[0];
      const userAddressString = [
        userAddress.doorNumber,
        userAddress.buildingName,
        userAddress.address1,
        userAddress.address2,
      ]
        .filter(Boolean)
        .join(", ");

      getCoordinatesFromAddress(userAddressString)
        .then((userCoords) => {
          Promise.all(
            filteredProperties.map(async (property) => {
              if (!property.address) return null;
              try {
                const propertyCoords = await getCoordinatesFromAddress(
                  property.address
                );
                const distance = calculateDistance(
                  userCoords.lat,
                  userCoords.lng,
                  propertyCoords.lat,
                  propertyCoords.lng
                );
                // Only include properties within selectedDistance
                return distance <= selectedDistance ? property : null;
              } catch (error) {
                console.error(
                  `Error processing property ${property.name}:`,
                  error
                );
                return null;
              }
            })
          )
            .then((results) => {
              const filteredByDistance = results.filter(Boolean) as Property[];
              setProperties(filteredByDistance);
            })
            .catch((error) => {
              console.error("Error in distance filtering:", error);
            });
        })
        .catch((error) => {
          console.error("Error geocoding user address:", error);
        });
      return;
    }

    // Update displayed properties
    setProperties(filteredProperties);
  }, [
    allProperties,
    selectedCity,
    selectedRoomTypes,
    selectedLocations,
    selectedCategories,
    selectedPropertyTypes,
    selectedDistance,
    userRole,
    userDetails,
    getCoordinatesFromAddress,
    calculateDistance,
  ]);

  // Handler for clearing all filters
  const handleClearFilters = useCallback(() => {
    setSelectedCity(null);
    setSelectedRoomTypes([]);
    setSelectedLocations([]);
    setSelectedCategories([]);
    setSelectedPropertyTypes([]);
    setSelectedDistance(null);

    // Reset properties to show all
    setProperties([...allProperties]);
  }, [allProperties]);

  // Auto-apply filters when any filter selection changes
  useEffect(() => {
    // Only auto-apply if we have properties loaded
    if (allProperties.length > 0) {
      handleSearch();
    }
  }, [
    selectedCity,
    selectedLocations,
    selectedPropertyTypes,
    selectedRoomTypes,
    selectedCategories,
    selectedDistance,
    allProperties,
    handleSearch,
  ]);

  return (
    <>
      <div className="content">
        <div className="content-body">
          <div className="row">
            <div className="col-md-2">
              <PropertyFilter
                cities={cities}
                locations={locations}
                propertyTypes={propertyTypes}
                roomTypes={roomTypes}
                categories={categories}
                selectedCity={selectedCity}
                selectedLocations={selectedLocations}
                selectedPropertyTypes={selectedPropertyTypes}
                selectedRoomTypes={selectedRoomTypes}
                selectedCategories={selectedCategories}
                selectedDistance={selectedDistance}
                onCityChange={setSelectedCity}
                onLocationsChange={setSelectedLocations}
                onPropertyTypesChange={setSelectedPropertyTypes}
                onRoomTypesChange={setSelectedRoomTypes}
                onCategoriesChange={setSelectedCategories}
                onDistanceChange={setSelectedDistance}
                onApplyFilters={handleSearch}
                onClearFilters={handleClearFilters}
              />
            </div>
            <div className="col-md-6">
              <PropertyGrid
                properties={properties}
                allProperties={allProperties}
                loading={loading}
                userDetails={userDetails}
                onPropertiesChange={setProperties}
                dateRange={dateRange}
                selectedRoomType={selectedRoomType}
                selectedRoomTypes={selectedRoomTypes}
                onPropertyMapView={handlePropertyMapView}
                calculateDistanceToProperty={calculateDistanceToProperty}
                onPropertyRouteView={handlePropertyRouteView}
              />
            </div>
            <div className="col-md-4">
              <PropertyMap
                properties={properties}
                loading={loading}
                selectedPropertyId={selectedPropertyId}
                routeRequest={routeRequest}
              />
            </div>
          </div>
          <Footer />
        </div>
      </div>
    </>
  );
};
export default PropertyListBB;
